import {hasAdvertisingConsent, hubspotAPI} from '@commercial-website/consent'
import {useConsentPreferences} from '@commercial-website/consent/react'
import {LDProvider, useLDClient} from 'launchdarkly-react-client-sdk'
import React from 'react'

import {resolveDeviceId} from './device-id'

/*
 * Defined event keys in LD. This type exists just to add some typesafety when calling `useFlagVariation()`
 */
type LaunchDarklyFlagKey = 'test-flag' | 'aa-commercial-website-test'

/*
 * Union of supported events defined in LaunchDarkly
 */
type LaunchDarklyExperimentEvent = 'email-signup-actioned'

// Context for providing flags loaded status
const FeatureFlagsLoadedStatusContext = React.createContext<{
    loaded: boolean
    setLoaded: (loaded: boolean) => void
}>({
    loaded: false,
    setLoaded: () => {},
})

// Provides the loading status of LaunchDarkly flags.
function FlagsLoadedStatusProvider({children}: {children: React.ReactNode}) {
    const [loaded, setLoaded] = React.useState(false)

    return (
        <FeatureFlagsLoadedStatusContext.Provider value={{loaded, setLoaded}}>
            {children}
        </FeatureFlagsLoadedStatusContext.Provider>
    )
}

// Handles the status of the LD client.
function HandleLDClientStatus() {
    const ldClient = useLDClient()
    const {setLoaded} = React.useContext(FeatureFlagsLoadedStatusContext)

    React.useEffect(() => {
        if (!ldClient) return
        setLoaded(true)
    }, [ldClient, setLoaded])

    return null
}

/**
 * Wraps the app in a pre-configured LaunchDarkly provider (https://github.com/launchdarkly/react-client-sdk)
 * Passes all the information necessary to calculate the value of all the flags to LD when initializing.
 * Re-initialises the LD client when advertisement consent changes, toggling event sending accordingly.
 * The shared `device` id (see `resolveDeviceId`) is resolved synchronously, before `LDProvider` is
 * rendered, so the SDK always initialises with the right `key` from its very first mount.
 */
export function LaunchDarklyProvider({
    clientSideID,
    baseUrl,
    streamUrl,
    eventsUrl,
    children,
}: {
    clientSideID: string
    baseUrl?: string
    streamUrl?: string
    eventsUrl?: string
    children: React.ReactNode
}) {
    const prefs = useConsentPreferences()
    const hasConsent = prefs.allowed || prefs.categories.advertisement
    const deviceId = resolveDeviceId({
        hasAdvertisingConsent,
        onConsentChange: hubspotAPI.addPrivacyConsentListener,
    })

    return (
        <LDProvider
            key={String(hasConsent)}
            clientSideID={clientSideID}
            context={
                deviceId
                    ? {
                          kind: 'device',
                          key: deviceId,
                          anonymous: false,
                      }
                    : undefined
            }
            options={{
                bootstrap: 'localStorage',
                diagnosticOptOut: true,
                fetchGoals: false,
                sendLDHeaders: false,
                sendEvents: hasConsent,
                streaming: false,
                baseUrl,
                streamUrl,
                eventsUrl,
            }}
        >
            <FlagsLoadedStatusProvider>
                <HandleLDClientStatus />
                {children}
            </FlagsLoadedStatusProvider>
        </LDProvider>
    )
}

/**
 * Hook to check if LaunchDarkly flags have loaded.
 */
export function useFeatureFlagsLoaded() {
    const {loaded} = React.useContext(FeatureFlagsLoadedStatusContext)
    return loaded
}

/**
 * Hook to retrieve a specific feature flag value. Also records an exposure
 * event if the flag is part of an active experiment.
 */
export function useFlagVariation<T>(flagKey: LaunchDarklyFlagKey, defaultValue: T) {
    const ldClient = useLDClient()
    const cacheRef = React.useRef<{
        ldClient: typeof ldClient
        value: T
        flagKey: LaunchDarklyFlagKey
    } | null>(null)

    return React.useCallback(() => {
        if (!ldClient) return defaultValue
        if (
            cacheRef.current?.value !== undefined &&
            cacheRef.current.ldClient === ldClient &&
            cacheRef.current.flagKey === flagKey
        ) {
            return cacheRef.current.value
        }

        const value = ldClient.variation(flagKey, defaultValue) as T
        cacheRef.current = {ldClient, value, flagKey}
        return value
    }, [defaultValue, ldClient, flagKey])
}

/**
 * Hook to track experiment events in LaunchDarkly (distinct from Segment tracking).
 */
export function useTrackExperimentEvent() {
    const ldClient = useLDClient()
    return React.useCallback(
        (eventKey: LaunchDarklyExperimentEvent, data?: unknown, metricValue?: number) => {
            ldClient?.track(eventKey, data, metricValue)
        },
        [ldClient],
    )
}
